'use client'

import { Title } from '@/components/common'
import { RHFSelect, RHFTextField } from '@/components/form'
import { RHFCheckbox } from '@/components/form/rhf-checkbox'
import { ButtonAddAddress } from '@/components/pages/checkout/button-add-address'
import { ItemAddressCheckout } from '@/components/pages/checkout/item-address-checkout'
import { useCities, useDistrict, useWards } from '@/hooks'
import { setShippingAddress, setShippingMethodAction } from '@/services'
import { useAccountStore, useCartStore } from '@/store'
import { Grid } from '@mui/material'
import isEmpty from 'lodash/isEmpty'
import { useTranslations } from 'next-intl'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Control, UseFormReset, UseFormSetValue, UseFormWatch } from 'react-hook-form'
import { LocationForm } from './LocationForm'
import { useAddressTypes } from '../../hooks'

interface Props {
  control: Control<IFormCheckout>
  watch: UseFormWatch<IFormCheckout>
  setShippingMethod: ({ method_code, carrier_code }: { method_code?: string; carrier_code?: string }) => void
  shippingMethod: { method_code?: string; carrier_code?: string } | undefined
  reset: UseFormReset<IFormCheckout>
  setValue: UseFormSetValue<IFormCheckout>
}
export enum AddressCode {
  CITY = 'province_code',
  DISTRICT = 'district_code',
  WARD = 'ward_code',
}
export const CheckoutInfoV2 = ({
  control,
  watch,
  setShippingMethod,
  reset,
  setValue,
  shippingMethod,
}: Props) => {
  const t = useTranslations()
  const { cartId, refetchCart, cartData } = useCartStore()
  const { data: userInfo } = useAccountStore()
  const [selectAddress, setSelectAddress] = useState<IAddressCustomer>({} as IAddressCustomer)
  const { data: listAddressTypes } = useAddressTypes()
  const currentAddressType = watch('addressType', '')
  const isExistDistrictField = useMemo(() => {
    const availableFields = listAddressTypes?.find((type) => type.type_code === currentAddressType)?.fields
    return !!availableFields?.find((field: any) => field.field_code === AddressCode.DISTRICT)
  }, [currentAddressType, listAddressTypes])
  const listFieldAddress = useMemo(() => {
    if (!listAddressTypes) return []
    return listAddressTypes.find((type) => type.type_code === currentAddressType)?.fields || []
  }, [listAddressTypes, currentAddressType])

  const { dataCities } = useCities(currentAddressType)
  const { dataDistricts } = useDistrict(
    currentAddressType,
    watch(AddressCode.CITY as any),
    isExistDistrictField
  )
  const { dataWards } = useWards(currentAddressType, watch(AddressCode.DISTRICT as any))

  const submitShippingMethod = useCallback(
    (data: { method_code?: string; carrier_code?: string }) => {
      const dataSubmit = {
        setShippingMethodsOnCartInput: {
          cart_id: cartId,
          shipping_methods: [
            {
              ...data,
            },
          ],
        },
      }
      setShippingMethodAction(dataSubmit).then((res) => {
        console.log('set shiping method', res)
        refetchCart()
      })
    },
    [cartId, refetchCart]
  )

  const onSubmitShippingAddress = async (data: any) => {
    let dataAddress: TDataAddress
    // tuỳ theo có đăng nhập hay không sẽ gửi data tương ứng
    if (!isEmpty(userInfo)) {
      dataAddress = {
        customer_address_id: selectAddress?.id || data,
      }
    } else {
      dataAddress = {
        // address: {
        //   country_code: 'VN',
        //   company: '',
        //   lastname: data?.name,
        //   firstname: '.',
        //   postcode: '7000',
        //   street: [data?.address],
        //   telephone: data?.phone,
        //   save_in_address_book: false,
        //   city: dataCities?.find((city) => city.city_id == data?.city_id)?.default_name || '.',
        //   custom_attributes: [
        //     {
        //       attribute_code: 'ward',
        //       value: dataWards?.find((ward) => ward.ward_id == data?.ward_id)?.default_name || '.',
        //     },
        //     {
        //       attribute_code: 'ward_id',
        //       value: data?.ward_id,
        //     },
        //     {
        //       attribute_code: 'district',
        //       value:
        //         dataDistricts?.find((district) => district.district_id == data?.district_id)?.default_name ||
        //         '.',
        //     },
        //     {
        //       attribute_code: 'district_id',
        //       value: data?.district_id,
        //     },
        //     {
        //       attribute_code: 'city_id',
        //       value: data?.city_id,
        //     },
        //   ],
        // },
      }
    }

    const dataSubmit: IShippingAddressPayload = {
      setBillingAddressOnCartInput: {
        cart_id: cartId,
        billing_address: {
          same_as_shipping: true,
        },
      },
      setShippingAddressesOnCartInput: {
        cart_id: cartId,
        shipping_addresses: [
          {
            customer_notes: data.note,
            ...dataAddress,
          },
        ],
      },
    }
    await setShippingAddress(dataSubmit)
      .then((res) => {
        console.log('set shiping address', res)
        refetchCart()
      })
      .then(() => {
        setShippingMethod({ carrier_code: 'flatrate', method_code: 'flatrate' })
        submitShippingMethod({
          carrier_code: shippingMethod?.carrier_code,
          method_code: shippingMethod?.method_code,
        })
      })
  }

  const handleSelectAddress = (e: any, data: IAddressCustomer) => {
    if ((!!e && e?.target?.closest('.btn-del')) || e?.target?.closest('.btn-change')) return
    setSelectAddress(data)
    onSubmitShippingAddress(data?.id)
  }

  const listAddress = useCallback(() => {
    return userInfo?.addresses
  }, [userInfo?.addresses])

  useEffect(() => {
    if (!!userInfo?.addresses?.length) {
      if (userInfo?.default_shipping) {
        handleSelectAddress(
          null,
          listAddress().find((el) => el.id == userInfo?.default_shipping) || listAddress()[0]
        )
      } else {
        handleSelectAddress(null, listAddress()?.[0])
      }
    }
  }, [listAddress])

  return (
    <Grid spacing={1} columns={24} container>
      <Grid
        size={{
          xs: 24,
          md: 24,
          lg: 24,
        }}
      >
        <Title variants="h3">{t('Shipping address')}</Title>
      </Grid>
      <Grid
        size={{
          xs: 24,
          md: 12,
          lg: 12,
        }}
      >
        <RHFTextField name="name" label={t('Fullname')} placeholder="Nguyen Van A" />
      </Grid>
      <Grid
        size={{
          xs: 24,
          md: 12,
          lg: 12,
        }}
      >
        <RHFTextField name="phone" label={t('Phone number')} placeholder="029083982" />
      </Grid>
      <Grid
        size={{
          xs: 24,
        }}
      >
        <RHFTextField name="email" label={t('Email')} placeholder="example@gmail.com" />
      </Grid>
      {userInfo?.addresses?.length ? (
        <>
          <Grid container spacing={1} columns={24}>
            {userInfo?.addresses?.map((address) => (
              <ItemAddressCheckout
                address={address}
                key={address?.id}
                selectAddress={selectAddress}
                listCities={dataCities}
                handleSelectAddtress={handleSelectAddress}
              />
            ))}
          </Grid>
          <ButtonAddAddress listCities={dataCities} />
        </>
      ) : (
        // <LocationForm
        //   dataCities={dataCities}
        //   dataDistricts={dataDistricts}
        //   dataWards={dataWards}
        //   setValue={setValue}
        // />
        <></>
      )}
      <Grid
        size={{
          xs: 24,
          md: 24,
          lg: 24,
        }}
      >
        <RHFTextField name="note" label={t('Order note')} placeholder={t('Order note')} rows={4} />
      </Grid>
      <Grid
        size={{
          xs: 24,
          md: 24,
          lg: 24,
        }}
      >
        <RHFCheckbox
          slotProps={{ checkbox: { sx: { pl: 0 } } }}
          name="rule"
          defaultChecked={false}
          label={t('Terms and regulations')}
        />
      </Grid>
      {/* <ShippingMethod
        setShippingMethod={setShippingMethod}
        shippingMethod={shippingMethod}
        submitShippingMethod={submitShippingMethod}
      /> */}
    </Grid>
  )
}
